import jsPDF from 'jspdf';
import { classifyStatus } from './checker';

export function downloadJSON(result) {
  const reportData = {
    ...result,
    generatedAt: new Date().toISOString(),
    version: '1.0.0',
    generator: 'Web Down Status'
  };

  const blob = new Blob([JSON.stringify(reportData, null, 2)], {
    type: 'application/json'
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `web-status-${result.input.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadPDF(result) {
  const pdf = new jsPDF();
  const margin = 20;
  let yPosition = margin;

  // Helper function to add text with word wrapping
  const addText = (text, x, y, maxWidth = 170) => {
    const lines = pdf.splitTextToSize(text, maxWidth);
    pdf.text(lines, x, y);
    return y + (lines.length * 7);
  };

  const status = classifyStatus(result);
  const isLimited = result.mode === 'Limited';
  const displayStatus = isLimited && status !== 'Down' ? `${status} (Limited)` : status;

  // Header
  pdf.setFontSize(20);
  pdf.setFont(undefined, 'bold');
  yPosition = addText('Web Down Status Report', margin, yPosition);
  
  yPosition += 10;
  
  // Basic Info
  pdf.setFontSize(16);
  pdf.setFont(undefined, 'bold');
  yPosition = addText('Basic Information', margin, yPosition);
  
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'normal');
  yPosition += 5;
  
  yPosition = addText(`Website: ${result.input}`, margin, yPosition);
  yPosition = addText(`Mode: ${result.mode}${result.proxyUsed ? ' (Proxy)' : ''}`, margin, yPosition);
  yPosition = addText(`Status: ${displayStatus}`, margin, yPosition);
  yPosition = addText(`Response Time: ${result.responseTimeMs}ms`, margin, yPosition);
  yPosition = addText(`HTTP Status: ${result.httpCode || 'N/A'}`, margin, yPosition);
  yPosition = addText(`Final URL: ${result.finalUrl}`, margin, yPosition);
  yPosition = addText(`CORS Blocked: ${result.corsBlocked ? 'Yes' : 'No'}`, margin, yPosition);
  yPosition = addText(`Checked: ${new Date(result.timestamp).toLocaleString()}`, margin, yPosition);
  
  yPosition += 10;

  // Limited Check Results
  if (isLimited) {
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    yPosition = addText('Limited Check Results', margin, yPosition);
    
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'normal');
    yPosition += 5;
    
    yPosition = addText(`DNS Resolution: ${result.dnsResolved ? 'Success' : 'Failed'}`, margin, yPosition);
    yPosition = addText(`Favicon Test: ${result.faviconReachable ? 'Success' : 'Failed'}`, margin, yPosition);
    
    yPosition += 10;
  }

  // DNS Information
  if (result.dns && result.dns.aRecords && result.dns.aRecords.length > 0) {
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    yPosition = addText('DNS Information', margin, yPosition);
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    yPosition += 5;
    
    result.dns.aRecords.forEach((ip) => {
      yPosition = addText(`A Record: ${ip}`, margin, yPosition);
    });
    
    if (result.dns.ttl) {
      yPosition = addText(`TTL: ${result.dns.ttl} seconds`, margin, yPosition);
    }
    
    yPosition += 10;
  }

  // Headers section (only if not limited or if headers are available)
  if (result.headers && Object.keys(result.headers).length > 0) {
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    yPosition = addText('Response Headers', margin, yPosition);
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    yPosition += 5;
    
    Object.entries(result.headers).forEach(([key, value]) => {
      yPosition = addText(`${key}: ${value}`, margin, yPosition, 170);
      yPosition += 2;
    });
    
    yPosition += 10;
  }

  // Redirects section
  if (result.redirects && result.redirects.length > 0) {
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    yPosition = addText(`Redirect Chain (${result.redirects.length})`, margin, yPosition);
    
    pdf.setFontSize(10);
    pdf.setFont(undefined, 'normal');
    yPosition += 5;
    
    result.redirects.forEach((redirect, index) => {
      yPosition = addText(`${index + 1}. ${redirect.from} → ${redirect.to} (HTTP ${redirect.code})`, margin, yPosition, 170);
      yPosition += 2;
    });
    
    yPosition += 10;
  }

  // Security Info
  pdf.setFontSize(16);
  pdf.setFont(undefined, 'bold');
  yPosition = addText('Security Information', margin, yPosition);
  
  pdf.setFontSize(12);
  pdf.setFont(undefined, 'normal');
  yPosition += 5;
  
  yPosition = addText(`HTTPS: ${result.finalUrl?.startsWith('https://') ? 'Yes' : 'No'}`, margin, yPosition);
  yPosition = addText(`CORS Status: ${result.corsBlocked ? 'Blocked' : 'Allowed'}`, margin, yPosition);
  yPosition = addText(`TLS Certificate: ${result.finalUrl?.startsWith('https://') ? 'Browser Verified' : 'N/A'}`, margin, yPosition);
  
  if (result.reason) {
    yPosition += 5;
    pdf.setFontSize(16);
    pdf.setFont(undefined, 'bold');
    yPosition = addText('Error Details', margin, yPosition);
    
    pdf.setFontSize(12);
    pdf.setFont(undefined, 'normal');
    yPosition += 5;
    yPosition = addText(result.reason, margin, yPosition, 170);
  }

  // Footer
  const pageHeight = pdf.internal.pageSize.height;
  pdf.setFontSize(8);
  pdf.setFont(undefined, 'italic');
  pdf.text('Generated locally by Web Down Status', margin, pageHeight - 10);
  pdf.text(new Date().toLocaleString(), margin, pageHeight - 5);

  // Save the PDF
  pdf.save(`web-status-${result.input.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`);
}